import { Component } from '@theme/component';

/**
 * Audio Player — minimal custom player.
 *
 * Renders no markup itself; the Liquid snippet provides:
 * - a hidden native <audio> element (the source of truth)
 * - a play/pause toggle button
 * - a native <input type="range"> styled as a thin line + circular thumb (seek)
 * - a time readout that shows the REMAINING time and counts down to 0:00
 *
 * Accessibility: the range input is keyboard-operable (native), the toggle is a
 * real <button>, and the visible time is aria-hidden (the range announces position).
 *
 * @typedef {{
 *   audio: HTMLAudioElement,
 *   toggle: HTMLButtonElement,
 *   scrubber: HTMLInputElement,
 *   time: HTMLElement
 * }} AudioPlayerRefs
 * @extends {Component<AudioPlayerRefs>}
 */
class AudioPlayer extends Component {
	requiredRefs = ['audio', 'toggle', 'scrubber', 'time'];

	/** @type {AbortController | null} */
	#controller = null;

	connectedCallback() {
		super.connectedCallback();

		const { audio } = this.refs;
		this.#controller = new AbortController();
		const { signal } = this.#controller;

		audio.addEventListener('loadedmetadata', this.#handleLoaded, { signal });
		audio.addEventListener('timeupdate', this.#handleTimeUpdate, { signal });
		audio.addEventListener('ended', this.#handleEnded, { signal });
		audio.addEventListener('pause', this.#syncToggle, { signal });
		audio.addEventListener('play', this.#syncToggle, { signal });

		// Metadata may already be available (cached / fast connection).
		if (audio.readyState >= 1) this.#handleLoaded();
	}

	disconnectedCallback() {
		super.disconnectedCallback();
		this.#controller?.abort();
		this.#controller = null;
	}

	/**
	 * Play/pause — bound via on:click="/toggle" on the button.
	 */
	toggle() {
		const { audio } = this.refs;
		if (audio.paused) {
			audio.play().catch(() => {});
		} else {
			audio.pause();
		}
	}

	/**
	 * Seek to the scrubber position — bound via on:input="/seek" on the range.
	 */
	seek() {
		const { audio, scrubber } = this.refs;
		const value = Number(scrubber.value);
		if (Number.isFinite(value)) audio.currentTime = value;
	}

	#handleLoaded = () => {
		const { audio, scrubber } = this.refs;
		const duration = Number.isFinite(audio.duration) ? audio.duration : 0;
		scrubber.max = String(duration);
		scrubber.value = String(audio.currentTime || 0);
		this.#renderTime();
	};

	#handleTimeUpdate = () => {
		const { audio, scrubber } = this.refs;
		scrubber.value = String(audio.currentTime);
		this.#renderTime();
	};

	#handleEnded = () => {
		const { audio, scrubber } = this.refs;
		audio.currentTime = 0;
		scrubber.value = '0';
		this.#renderTime();
		this.#syncToggle();
	};

	/**
	 * Reflect the audio element's playing state on the host + toggle button.
	 */
	#syncToggle = () => {
		const { audio, toggle } = this.refs;
		const playing = !audio.paused;
		this.toggleAttribute('data-playing', playing);
		const label = playing ? this.dataset.pauseLabel : this.dataset.playLabel;
		if (label) toggle.setAttribute('aria-label', label);
	};

	/**
	 * Show the remaining time (counts down to 0:00).
	 */
	#renderTime() {
		const { audio, time } = this.refs;
		const duration = Number.isFinite(audio.duration) ? audio.duration : 0;
		const remaining = Math.max(0, duration - audio.currentTime);
		time.textContent = this.#format(remaining);
	}

	/**
	 * Format seconds as m:ss.
	 * @param {number} seconds
	 * @returns {string}
	 */
	#format(seconds) {
		const total = Math.round(seconds);
		const minutes = Math.floor(total / 60);
		const secs = total % 60;
		return `${minutes}:${String(secs).padStart(2, '0')}`;
	}
}

if (!customElements.get('audio-player')) {
	customElements.define('audio-player', AudioPlayer);
}
